import type { Auth, User } from 'firebase/auth';
import { withTimeout } from '../core/utils/timeout';
import type { EmailMfaRequiredError, MfaRequiredError, MfaResolver } from './authTypes';
import type { AppUser } from '../types';
import { syncProfileAfterSignIn } from './authHelpers';
import {
  clearEmailMfaGatePending,
  markEmailMfaGatePending,
} from './emailMfaSession';

export async function loadAuth() {
  const [{ auth }, authApi] = await Promise.all([
    import('../config/firebase.js'),
    import('firebase/auth'),
  ]);
  return { auth, ...authApi };
}

const ID_TOKEN_TIMEOUT_MS = 6_000;
const PROFILE_DOC_TIMEOUT_MS = 4_000;

export async function mapFirebaseUser(fbUser: User): Promise<AppUser> {
  const token = await withTimeout(
    fbUser.getIdTokenResult(),
    ID_TOKEN_TIMEOUT_MS,
    'id-token-timeout',
  );
  const role = token.claims.role;
  if (!role || typeof role !== 'string') {
    throw Object.assign(new Error('Account is not provisioned. Contact an administrator.'), {
      code: 'auth/not-provisioned',
    });
  }
  const houseIdRaw = token.claims.houseId;
  const houseId = typeof houseIdRaw === 'string' ? houseIdRaw : '';
  const houseIds = Array.isArray(token.claims.houseIds)
    ? token.claims.houseIds.map(String).filter(Boolean)
    : houseId
      ? [houseId]
      : [];

  let name = fbUser.displayName || fbUser.email?.split('@')[0] || 'User';
  let alias: string | undefined;
  try {
    const { db } = await import('../config/firebase.js');
    const { doc, getDoc } = await import('firebase/firestore');
    const snap = await withTimeout(
      getDoc(doc(db, 'users', fbUser.uid)),
      PROFILE_DOC_TIMEOUT_MS,
      'profile-read-timeout',
    );
    if (snap.exists()) {
      const data = snap.data() as { name?: unknown; alias?: unknown };
      if (typeof data.name === 'string' && data.name.trim()) name = data.name.trim();
      if (typeof data.alias === 'string' && data.alias.trim()) alias = data.alias.trim();
    }
  } catch {
    // Profile read is best-effort; Auth displayName remains the fallback.
  }

  const { resolveUserLabel } = await import('../core/rules/userLabel');
  return {
    id: fbUser.uid,
    name: resolveUserLabel({ name, alias }),
    alias,
    role: role as AppUser['role'],
    houseId: String(houseId),
    houseIds,
    email: fbUser.email ?? '',
  };
}

export type MultiFactorResolverFn = (auth: Auth, error: unknown) => MfaResolver;

export function wrapMfaError(
  err: unknown,
  auth: Auth,
  getMultiFactorResolver: MultiFactorResolverFn,
): never {
  const code = (err as { code?: string })?.code;
  if (code === 'auth/multi-factor-auth-required') {
    const error: MfaRequiredError = {
      code: 'mfa-required',
      resolver: getMultiFactorResolver(auth, err) as MfaResolver,
    };
    throw error;
  }
  throw err;
}

async function ensureEmailMfaIfRequired(email: string) {
  const { auth } = await loadAuth();
  const token = await auth.currentUser?.getIdTokenResult().catch(() => null);
  // God uses Auth App / SMS / support — never mailbox email MFA.
  if (String(token?.claims?.role ?? '') === 'god') return;

  const { getEmailMfaStatus } = await import('./EmailMfaService');
  const status = await getEmailMfaStatus();
  if (!status.enrolled) return;
  // Do not email a code here — the MFA sheet sends only on explicit user action.
  const error: EmailMfaRequiredError = {
    code: 'email-mfa-required',
    email,
  };
  throw error;
}

export async function finishPrimarySignIn(email: string) {
  const { auth } = await loadAuth();
  const uid = auth.currentUser?.uid?.trim() ?? '';
  // Block shell entry until email-MFA is resolved (required challenge or cleared).
  if (uid) markEmailMfaGatePending(uid);
  try {
    await ensureEmailMfaIfRequired(email);
  } catch (err) {
    const code = (err as { code?: string })?.code;
    if (code === 'email-mfa-required') throw err;
    clearEmailMfaGatePending();
    throw err;
  }
  clearEmailMfaGatePending();
  return syncProfileAfterSignIn(mapFirebaseUser, auth);
}
